"use client"

import { useEffect, useState } from "react"

type Section = {
   id: string
   title: string
   level?: 2 | 3
}

type LegalTableOfContentsProps = {
   sections: Section[]
}

function LegalTableOfContents({ sections }: LegalTableOfContentsProps) {
   const [activeSection, setActiveSection] = useState(sections[0]?.id ?? "")

   useEffect(() => {
      if (!sections.length) return

      const updateActiveSection = () => {
         const midpoint = window.innerHeight / 2
         let nextActiveSection = sections[0].id
         let closestDistance = Number.POSITIVE_INFINITY

         for (const section of sections) {
            const element = document.getElementById(section.id)

            if (!element) continue

            const rect = element.getBoundingClientRect()
            const distance = Math.abs(rect.top - midpoint)

            if (distance < closestDistance) {
               closestDistance = distance
               nextActiveSection = section.id
            }
         }

         setActiveSection(nextActiveSection)
      }

      updateActiveSection()
      window.addEventListener("scroll", updateActiveSection, { passive: true })
      window.addEventListener("resize", updateActiveSection)

      return () => {
         window.removeEventListener("scroll", updateActiveSection)
         window.removeEventListener("resize", updateActiveSection)
      }
   }, [sections])

   if (!sections.length) return null

   return (
      <aside className="sticky top-28 hidden self-start lg:block lg:w-72 xl:w-80">
         <h3 className="mb-6 font-semibold after:mt-3 after:block after:h-1 after:w-16 after:bg-brandOrange after:content-['']">
            Table of contents
         </h3>
         <div className="rounded-lg border border-black/10 bg-white/85 shadow-sm backdrop-blur-sm">
            <nav aria-label="Table of contents">
               <ol>
                  {sections.map((section, index) => {
                     const isActive = section.id === activeSection

                     return (
                        <li key={section.id} className={`${index !== sections.length - 1 ? "border-b-[1px]" : ""}`}>
                           <a
                              href={`#${section.id}`}
                              aria-current={isActive ? "location" : undefined}
                              className={[
                                 "block border-l-[7px] py-3 text-custom-base-normal font-bold transition-colors duration-150",
                                 index === 0 ? "rounded-t-lg" : "",
                                 index === sections.length - 1 ? "rounded-b-lg" : "",
                                 section.level === 3 ? "pl-5" : "pl-3",
                                 isActive
                                    ? "border-brandOrange bg-brandOrange/15 text-black"
                                    : "border-transparent  text-black/55 hover:border-black/20 hover:text-black/80",
                              ].join(" ")}
                              onClick={(e) => {
                                 e.preventDefault()
                                 const el = document.getElementById(section.id)
                                 if (el) {
                                    el.scrollIntoView({ behavior: "smooth", block: "center" })
                                 }
                              }}
                           >
                              {section.title}
                           </a>
                        </li>
                     )
                  })}
               </ol>
            </nav>
         </div>
      </aside>
   )
}

export default LegalTableOfContents
